import React, { useEffect, useState } from 'react';
import { Plus, Edit2, Trash2, Loader2, Ticket } from 'lucide-react';
import { PromoAPI, formatPrice } from '../../lib/api';
import { useToast } from '../../hooks/use-toast';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';

const EMPTY = { code: '', discount_pct: '', discount_fixed: '', free_shipping: false, min_order: 0, expires_at: '', active: true };

const PromoCodesTab = () => {
  const { toast } = useToast();
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const load = async () => { setLoading(true); try { setCodes(await PromoAPI.adminList()); } finally { setLoading(false); } };
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(EMPTY); setEditing('new'); };
  const openEdit = (c) => {
    setForm({
      code: c.code,
      discount_pct: c.discount_pct ?? '',
      discount_fixed: c.discount_fixed ?? '',
      free_shipping: !!c.free_shipping,
      min_order: c.min_order || 0,
      expires_at: c.expires_at || '',
      active: c.active !== false,
    });
    setEditing(c);
  };
  const close = () => { setEditing(null); setForm(EMPTY); };

  const save = async () => {
    if (!form.code.trim()) { toast({ title: 'Code requis' }); return; }
    const payload = {
      code: form.code.trim(),
      discount_pct: form.discount_pct === '' ? null : Number(form.discount_pct),
      discount_fixed: form.discount_fixed === '' ? null : Number(form.discount_fixed),
      free_shipping: form.free_shipping,
      min_order: Number(form.min_order) || 0,
      expires_at: form.expires_at || null,
      active: form.active,
    };
    setBusy(true);
    try {
      if (editing === 'new') await PromoAPI.create(payload);
      else await PromoAPI.update(editing.code, payload);
      toast({ title: '✅ Code sauvegardé' });
      close();
      load();
    } catch (e) {
      toast({ title: 'Erreur', description: e?.response?.data?.detail || 'Sauvegarde échouée' });
    } finally { setBusy(false); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Supprimer "${c.code}" ?`)) return;
    await PromoAPI.remove(c.code);
    toast({ title: 'Code supprimé' });
    load();
  };

  return (
    <div>
      <div className="flex justify-end mb-4">
        <Button onClick={openNew} className="bg-[#1F4D3F] hover:bg-[#173B30] text-white" data-testid="promo-new">
          <Plus className="w-4 h-4 mr-1" /> Nouveau code
        </Button>
      </div>
      <div className="bg-white border border-[#E8E0D0] rounded-2xl overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-[#7A6F5C]"><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Chargement...</div>
        ) : codes.length === 0 ? (
          <div className="p-10 text-center text-[#7A6F5C]"><Ticket className="w-10 h-10 mx-auto mb-3 opacity-50" />Aucun code promo. Créez-en un pour booster vos ventes !</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[#FAF7F2] text-left text-[#7A6F5C] text-xs uppercase tracking-wider">
              <tr><th className="px-4 py-3">Code</th><th className="px-4 py-3">Remise</th><th className="px-4 py-3">Min commande</th><th className="px-4 py-3">Expire</th><th className="px-4 py-3">Utilisations</th><th className="px-4 py-3">Statut</th><th className="px-4 py-3 text-right"></th></tr>
            </thead>
            <tbody>
              {codes.map((c) => {
                const remise = [
                  c.discount_pct ? `${c.discount_pct}%` : null,
                  c.discount_fixed ? `${formatPrice(c.discount_fixed)}` : null,
                  c.free_shipping ? 'Livraison gratuite' : null,
                ].filter(Boolean).join(' + ') || '—';
                return (
                  <tr key={c.code} className="border-t border-[#F5F1EA]" data-testid={`promo-row-${c.code}`}>
                    <td className="px-4 py-3 font-mono font-medium text-[#1F4D3F]">{c.code}</td>
                    <td className="px-4 py-3 text-[#5A5247]">{remise}</td>
                    <td className="px-4 py-3 text-[#5A5247]">{c.min_order ? formatPrice(c.min_order) : '—'}</td>
                    <td className="px-4 py-3 text-[#5A5247] text-xs">{c.expires_at ? new Date(c.expires_at).toLocaleDateString('fr-FR') : '—'}</td>
                    <td className="px-4 py-3 text-[#5A5247]">{c.uses_count || 0}</td>
                    <td className="px-4 py-3"><span className={`text-xs px-2 py-0.5 rounded ${c.active ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-600'}`}>{c.active ? 'Actif' : 'Inactif'}</span></td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => openEdit(c)} className="p-1.5 hover:bg-[#F5F1EA] rounded text-[#1F4D3F]" data-testid={`promo-edit-${c.code}`}><Edit2 className="w-4 h-4" /></button>
                      <button onClick={() => remove(c)} className="p-1.5 hover:bg-rose-50 rounded text-rose-600 ml-1"><Trash2 className="w-4 h-4" /></button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <Dialog open={editing !== null} onOpenChange={(o) => !o && close()}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editing === 'new' ? 'Nouveau code promo' : 'Modifier le code'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-sm text-[#2D2A26] mb-1.5 block">Code *</label>
              <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="BIENVENUE2026" data-testid="promo-form-code" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm text-[#2D2A26] mb-1.5 block">Remise %</label>
                <Input type="number" min="0" max="100" value={form.discount_pct} onChange={(e) => setForm({ ...form, discount_pct: e.target.value })} placeholder="10" />
              </div>
              <div>
                <label className="text-sm text-[#2D2A26] mb-1.5 block">Remise fixe (DA)</label>
                <Input type="number" min="0" value={form.discount_fixed} onChange={(e) => setForm({ ...form, discount_fixed: e.target.value })} placeholder="500" />
              </div>
              <div>
                <label className="text-sm text-[#2D2A26] mb-1.5 block">Min. commande (DA)</label>
                <Input type="number" min="0" value={form.min_order} onChange={(e) => setForm({ ...form, min_order: e.target.value })} placeholder="0" />
              </div>
              <div>
                <label className="text-sm text-[#2D2A26] mb-1.5 block">Date d'expiration</label>
                <Input type="date" value={form.expires_at ? form.expires_at.slice(0, 10) : ''} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} />
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.free_shipping} onChange={(e) => setForm({ ...form, free_shipping: e.target.checked })} className="w-4 h-4 rounded text-[#1F4D3F]" /> Livraison gratuite
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="w-4 h-4 rounded text-[#1F4D3F]" /> Actif
            </label>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={close}>Annuler</Button>
            <Button onClick={save} disabled={busy} className="bg-[#1F4D3F] hover:bg-[#173B30] text-white" data-testid="promo-form-save">{busy ? 'Sauvegarde...' : 'Enregistrer'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PromoCodesTab;
